import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  ArrowLeft, ChevronDown, ChevronUp, Loader2, Shuffle, Sparkles,
  AlertCircle, RotateCcw, Play, History,
} from 'lucide-react';
import { questionHelperAPI, handleAPIError } from '../services/api';
import { getDeviceId } from '../lib/deviceId';
import {
  bankUniverse, topicsForPaper, availableMarks, availableCommandTerms,
  availableDifficulties, filterQuestions, summarise, buildSession,
  saveFilterPrefs, getFilterPrefs, getActiveSession, saveActiveSession,
  clearActiveSession, PAPERS,
} from '../lib/qhSession';

const SESSION_ROUTE = '/igcse-geography/question-helper/session';
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const LENGTHS = [5, 10, 15, 20, 'all'];
const ORDERS = [
  { v: 'random', label: 'Random order' },
  { v: 'group_by_topic', label: 'Group by topic' },
  { v: 'marks_asc', label: 'Lowest to highest marks' },
  { v: 'marks_desc', label: 'Highest to lowest marks' },
];
const QTYPES = [
  { v: 'all', label: 'All question types' },
  { v: 'case_study', label: 'Case-study / named-example' },
  { v: 'non_case_study', label: 'Non-case-study' },
];
const PROGRESS = [
  { v: 'all', label: 'All questions' },
  { v: 'unseen', label: 'Unseen' },
  { v: 'needs_practice', label: 'Needs practice' },
  { v: 'mastered', label: 'Mastered' },
];

const ChoiceButton = ({ active, onClick, children, testid, ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    aria-label={ariaLabel}
    data-testid={testid}
    className={`min-h-[44px] rounded-lg border px-3 py-2 text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
      active
        ? 'border-blue-600 bg-blue-600 text-white shadow'
        : 'border-slate-300 bg-white text-slate-700 hover:border-blue-400'
    }`}
  >
    {active ? '✓ ' : ''}{children}
  </button>
);

const QuestionHelperSetup = () => {
  const navigate = useNavigate();
  const deviceId = useMemo(() => getDeviceId(), []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [resumeSession, setResumeSession] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // filter state
  const [paper, setPaper] = useState('all');
  const [topics, setTopics] = useState([]);
  const [marks, setMarks] = useState([]);
  const [commandTerms, setCommandTerms] = useState([]);
  const [difficulties, setDifficulties] = useState([]);
  const [questionType, setQuestionType] = useState('all');
  const [progress, setProgress] = useState('all');
  const [length, setLength] = useState(10);
  const [order, setOrder] = useState('random');

  // ---- derived availability ----
  const topicGroups = useMemo(() => topicsForPaper(questions, paper), [questions, paper]);
  const availTopics = useMemo(() => topicGroups.flatMap((g) => g.topics), [topicGroups]);
  const availMarks = useMemo(() => availableMarks(questions, paper, topics), [questions, paper, topics]);
  const availCmd = useMemo(() => availableCommandTerms(questions, paper, topics, marks), [questions, paper, topics, marks]);
  const availDiff = useMemo(() => availableDifficulties(questions, paper, topics, marks, commandTerms), [questions, paper, topics, marks, commandTerms]);

  const filters = { paper, topics, marks, commandTerms, difficulties, questionType, progress, length, order };
  const matched = useMemo(() => filterQuestions(questions, filters, progressMap), [questions, paper, topics, marks, commandTerms, difficulties, questionType, progress, progressMap]);
  const summary = useMemo(() => summarise(matched, topics.length), [matched, topics.length]);

  const allForPaper = (p) => {
    const t = topicsForPaper(questions, p).flatMap((g) => g.topics);
    const m = availableMarks(questions, p, t);
    const c = availableCommandTerms(questions, p, t, m);
    const d = availableDifficulties(questions, p, t, m, c);
    return { topics: t, marks: m, commandTerms: c, difficulties: d };
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await questionHelperAPI.seed();
        const [practiceRes, progRes] = await Promise.all([
          questionHelperAPI.getPractice(),
          questionHelperAPI.getProgress(deviceId).catch(() => ({ progress: [] })),
        ]);
        const qs = practiceRes.questions || [];
        setQuestions(qs);
        const pm = {};
        (progRes.progress || []).forEach((p) => { pm[p.question_id] = p; });
        setProgressMap(pm);

        const u = bankUniverse(qs);
        const prefs = getFilterPrefs();
        if (prefs) {
          setPaper(prefs.paper || 'all');
          setTopics(prefs.topics || u.topics);
          setMarks(prefs.marks || u.marks);
          setCommandTerms(prefs.commandTerms || u.commandTerms);
          setDifficulties(prefs.difficulties || u.difficulties);
          setQuestionType(prefs.questionType || 'all');
          setProgress(prefs.progress || 'all');
          setLength(prefs.length || 10);
          setOrder(prefs.order || 'random');
        } else {
          setTopics(u.topics); setMarks(u.marks); setCommandTerms(u.commandTerms); setDifficulties(u.difficulties);
        }

        const s = getActiveSession();
        if (s && s.status !== 'completed' && s.question_ids && s.question_ids.length) {
          setResumeSession(s);
        }
      } catch (e) {
        setError(handleAPIError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [deviceId]);

  // ---- handlers ----
  const changePaper = (p) => {
    setPaper(p);
    const a = allForPaper(p);
    setTopics(a.topics); setMarks(a.marks); setCommandTerms(a.commandTerms); setDifficulties(a.difficulties);
  };
  const toggle = (arr, setArr, val) => setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const resetFilters = () => {
    const u = bankUniverse(questions);
    setPaper('all'); setTopics(u.topics); setMarks(u.marks); setCommandTerms(u.commandTerms);
    setDifficulties(u.difficulties); setQuestionType('all'); setProgress('all'); setLength(10); setOrder('random');
  };

  const applyPreset = (preset) => {
    const u = bankUniverse(questions);
    if (preset === 'quick') {
      setPaper('all'); setTopics(u.topics); setMarks(u.marks); setCommandTerms(u.commandTerms);
      setDifficulties(u.difficulties); setQuestionType('all'); setProgress('all'); setLength(5); setOrder('random');
    } else if (preset === 'extended') {
      const groups = topicsForPaper(questions, 'all').filter((g) => g.paper_code === 'P1' || g.paper_code === 'P2');
      const t = groups.flatMap((g) => g.topics);
      setPaper('all'); setTopics(t); setMarks([5, 7]); setCommandTerms(u.commandTerms);
      setDifficulties(u.difficulties); setQuestionType('all'); setProgress('all'); setLength(5); setOrder('random');
    } else if (preset === 'paper4') {
      const a = allForPaper('P4');
      setPaper('P4'); setTopics(a.topics); setMarks(a.marks); setCommandTerms(a.commandTerms);
      setDifficulties(a.difficulties); setQuestionType('all'); setProgress('all'); setLength(10); setOrder('random');
    } else if (preset === 'needs') {
      setPaper('all'); setTopics(u.topics); setMarks(u.marks); setCommandTerms(u.commandTerms);
      setDifficulties(u.difficulties); setQuestionType('all'); setProgress('needs_practice'); setLength(10); setOrder('random');
    }
  };

  const startWith = (f, matchedSet) => {
    if (!matchedSet.length) return;
    saveFilterPrefs(f);
    const session = buildSession(matchedSet, f);
    saveActiveSession(session);
    navigate(SESSION_ROUTE);
  };

  const startSession = () => startWith(filters, matched);

  const surprise = () => {
    const u = bankUniverse(questions);
    const f = {
      paper: 'all', topics: u.topics, marks: u.marks, commandTerms: u.commandTerms,
      difficulties: u.difficulties, questionType: 'all', progress: 'all', length: 10, order: 'random',
    };
    const m = filterQuestions(questions, f, progressMap);
    startWith(f, m);
  };

  const discard = () => { clearActiveSession(); setResumeSession(null); setConfirmDiscard(false); };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="flex items-center gap-3 text-slate-600" data-testid="qhs-loading">
          <Loader2 className="w-6 h-6 animate-spin" /> Loading your practice options…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-slate-100">
        <Card className="max-w-lg w-full"><CardContent className="p-6 text-center space-y-4">
          <AlertCircle className="w-10 h-10 text-red-600 mx-auto" />
          <p className="text-red-700" data-testid="qhs-error">{error}</p>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </CardContent></Card>
      </div>
    );
  }

  const zero = matched.length === 0;
  const PRESETS = [
    { id: 'quick', icon: Shuffle, title: 'Quick mixed practice', desc: 'All papers · 5 questions' },
    { id: 'extended', icon: Sparkles, title: 'Extended-response practice', desc: 'Papers 1 & 2 · 5 & 7 marks' },
    { id: 'paper4', icon: Play, title: 'Paper 4 fieldwork', desc: 'Paper 4 only · 10 questions' },
    { id: 'needs', icon: RotateCcw, title: 'Needs practice', desc: 'Questions to revisit · 10' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8" data-testid="qhs-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/')} data-testid="qhs-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to IGCSE Geography
          </Button>
        </div>

        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800">Build Your Question Practice</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Choose the topics, command terms and question styles you want to practise. You will still
            need to identify the examiner features and judge the mark awarded.
          </p>
        </div>

        {/* Resume panel */}
        {resumeSession && (
          <Card className="border-amber-300 bg-amber-50" data-testid="qhs-resume-panel">
            <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <History className="w-5 h-5 text-amber-700" />
                <span className="text-amber-900 font-medium">
                  Resume your previous session — {resumeSession.completed_question_ids?.length || 0} of {resumeSession.question_ids.length} questions completed.
                </span>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => navigate(SESSION_ROUTE)} data-testid="qhs-resume-btn">Resume session</Button>
                {!confirmDiscard ? (
                  <Button variant="outline" onClick={() => setConfirmDiscard(true)} data-testid="qhs-discard-btn">Discard</Button>
                ) : (
                  <Button variant="destructive" onClick={discard} data-testid="qhs-discard-confirm-btn">Confirm discard</Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Presets */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base md:text-lg font-semibold text-slate-700">Quick start</h2>
            <Button variant="outline" size="sm" onClick={surprise} data-testid="qhs-surprise">
              <Sparkles className="w-4 h-4 mr-1" /> Surprise me
            </Button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => applyPreset(p.id)}
                data-testid={`qhs-preset-${p.id}`}
                className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-blue-400 hover:shadow transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
              >
                <p.icon className="w-5 h-5 text-blue-600 mb-2" />
                <div className="font-semibold text-slate-800 text-sm">{p.title}</div>
                <div className="text-xs text-slate-500 mt-1">{p.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Paper */}
        <fieldset className="border-0">
          <legend className="text-base md:text-lg font-semibold text-slate-700 mb-2">Paper</legend>
          <div className="flex flex-wrap gap-2">
            {PAPERS.map((p) => (
              <ChoiceButton key={p.code} active={paper === p.code} onClick={() => changePaper(p.code)} testid={`qhs-paper-${p.code}`} ariaLabel={p.name}>
                {p.name}
              </ChoiceButton>
            ))}
          </div>
        </fieldset>

        {/* Topics */}
        <fieldset className="border-0">
          <div className="flex items-center justify-between mb-2">
            <legend className="text-base md:text-lg font-semibold text-slate-700">Topics</legend>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setTopics(availTopics)} data-testid="qhs-topics-select-all">Select all</Button>
              <Button variant="outline" size="sm" onClick={() => setTopics([])} data-testid="qhs-topics-clear">Clear</Button>
            </div>
          </div>
          <div className="space-y-3">
            {topicGroups.map((g) => (
              <div key={g.paper_code}>
                {paper === 'all' && <div className="text-xs font-semibold uppercase text-slate-400 mb-1">{g.paper_name}</div>}
                <div className="flex flex-wrap gap-2">
                  {g.topics.map((t) => {
                    const checked = topics.includes(t);
                    return (
                      <label
                        key={t}
                        data-testid={`qhs-topic-${slug(t)}`}
                        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer min-h-[44px] transition-all ${checked ? 'border-blue-600 bg-blue-50' : 'border-slate-300 bg-white hover:border-blue-400'}`}
                      >
                        <Checkbox checked={checked} onCheckedChange={() => toggle(topics, setTopics, t)} aria-label={t} />
                        <span className="text-slate-700">{t}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </fieldset>

        {/* Marks */}
        <fieldset className="border-0">
          <legend className="text-base md:text-lg font-semibold text-slate-700 mb-2">Marks available</legend>
          <div className="flex flex-wrap gap-2">
            {availMarks.map((m) => (
              <ChoiceButton key={m} active={marks.includes(m)} onClick={() => toggle(marks, setMarks, m)} testid={`qhs-mark-${m}`} ariaLabel={`${m} mark`}>
                {m} mark{m > 1 ? 's' : ''}
              </ChoiceButton>
            ))}
          </div>
        </fieldset>

        {/* Command terms */}
        <fieldset className="border-0">
          <legend className="text-base md:text-lg font-semibold text-slate-700 mb-2">Command terms</legend>
          <div className="flex flex-wrap gap-2">
            {availCmd.map((c) => (
              <ChoiceButton key={c} active={commandTerms.includes(c)} onClick={() => toggle(commandTerms, setCommandTerms, c)} testid={`qhs-cmd-${slug(c)}`} ariaLabel={c}>
                {c}
              </ChoiceButton>
            ))}
          </div>
        </fieldset>

        {/* Advanced options */}
        <div className="border rounded-xl bg-white">
          <button
            type="button"
            onClick={() => setAdvancedOpen((o) => !o)}
            aria-expanded={advancedOpen}
            data-testid="qhs-advanced-toggle"
            className="w-full flex items-center justify-between p-4 text-left font-semibold text-slate-700 min-h-[44px]"
          >
            Advanced options
            {advancedOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          {advancedOpen && (
            <div className="p-4 pt-0 space-y-5" data-testid="qhs-advanced">
              <fieldset className="border-0">
                <legend className="text-sm font-semibold text-slate-600 mb-2">Session length</legend>
                <div className="flex flex-wrap gap-2">
                  {LENGTHS.map((l) => (
                    <ChoiceButton key={l} active={length === l} onClick={() => setLength(l)} testid={`qhs-length-${l}`} ariaLabel={l === 'all' ? 'All matching questions' : `${l} questions`}>
                      {l === 'all' ? 'All matching' : `${l} questions`}
                    </ChoiceButton>
                  ))}
                </div>
              </fieldset>

              <fieldset className="border-0">
                <legend className="text-sm font-semibold text-slate-600 mb-2">Difficulty</legend>
                <div className="flex flex-wrap gap-2">
                  {availDiff.map((d) => (
                    <ChoiceButton key={d} active={difficulties.includes(d)} onClick={() => toggle(difficulties, setDifficulties, d)} testid={`qhs-diff-${slug(d)}`} ariaLabel={d}>
                      {d.charAt(0).toUpperCase() + d.slice(1)}
                    </ChoiceButton>
                  ))}
                </div>
              </fieldset>

              <fieldset className="border-0">
                <legend className="text-sm font-semibold text-slate-600 mb-2">Progress status</legend>
                <div className="flex flex-wrap gap-2">
                  {PROGRESS.map((p) => (
                    <ChoiceButton key={p.v} active={progress === p.v} onClick={() => setProgress(p.v)} testid={`qhs-progress-${p.v}`} ariaLabel={p.label}>
                      {p.label}
                    </ChoiceButton>
                  ))}
                </div>
              </fieldset>

              <fieldset className="border-0">
                <legend className="text-sm font-semibold text-slate-600 mb-2">Question type</legend>
                <div className="flex flex-wrap gap-2">
                  {QTYPES.map((p) => (
                    <ChoiceButton key={p.v} active={questionType === p.v} onClick={() => setQuestionType(p.v)} testid={`qhs-qtype-${p.v}`} ariaLabel={p.label}>
                      {p.label}
                    </ChoiceButton>
                  ))}
                </div>
              </fieldset>

              <fieldset className="border-0">
                <legend className="text-sm font-semibold text-slate-600 mb-2">Question order</legend>
                <div className="flex flex-wrap gap-2">
                  {ORDERS.map((p) => (
                    <ChoiceButton key={p.v} active={order === p.v} onClick={() => setOrder(p.v)} testid={`qhs-order-${p.v}`} ariaLabel={p.label}>
                      {p.label}
                    </ChoiceButton>
                  ))}
                </div>
              </fieldset>
            </div>
          )}
        </div>

        {/* Live summary */}
        <Card className={`border-0 shadow ${zero ? 'bg-red-50' : 'bg-white'}`} data-testid="qhs-summary">
          <CardContent className="p-4 space-y-2">
            {zero ? (
              <div className="space-y-2">
                <p className="font-semibold text-red-700" data-testid="qhs-summary-total">No questions match your selections.</p>
                <p className="text-sm text-red-600">
                  Try widening your filters — check that you have at least one topic, one mark total and one
                  command term selected for the chosen paper, and relax the progress or question-type filters.
                </p>
              </div>
            ) : (
              <>
                <p className="text-lg font-semibold text-slate-800" data-testid="qhs-summary-total">
                  {summary.total} question{summary.total === 1 ? '' : 's'} match your selections.
                </p>
                <div className="flex flex-wrap gap-2 text-sm">
                  {Object.entries(summary.byPaper).sort().map(([p, n]) => (
                    <Badge key={p} variant="secondary" data-testid={`qhs-summary-${p}`}>{n} {p}</Badge>
                  ))}
                  <Badge variant="outline">Marks included: {summary.marks.join(', ')}</Badge>
                  <Badge variant="outline">{summary.topicsSelected} topic{summary.topicsSelected === 1 ? '' : 's'} selected</Badge>
                </div>
                {length !== 'all' && summary.total < length && (
                  <p className="text-sm text-amber-700" data-testid="qhs-length-note">
                    Only {summary.total} questions match, so your session will use all {summary.total} (fewer than the {length} requested).
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pb-8">
          <Button
            onClick={startSession}
            disabled={zero}
            className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-700 text-white min-h-[48px] text-base"
            data-testid="qhs-start-btn"
          >
            <Play className="w-4 h-4 mr-2" /> Start question helper
          </Button>
          <Button variant="outline" onClick={resetFilters} className="min-h-[48px]" data-testid="qhs-reset-btn">
            <RotateCcw className="w-4 h-4 mr-1" /> Reset filters
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuestionHelperSetup;
